import React, { Component, ErrorInfo, ReactNode } from 'react'
import { cn } from 'utils/cn'
import { Button } from 'components/ui'
import { Renew, Home, Email } from '@carbon/icons-react'
import { post } from '../../../api/request'

interface Props {
  children: ReactNode
  fallback?: ReactNode
  onError?: (error: Error, errorInfo: ErrorInfo) => void
  showDetails?: boolean
}

interface State {
  hasError: boolean
  error: Error | null
  errorInfo: ErrorInfo | null
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    }
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
    }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Don't catch HTTP errors that should be handled as toasts
    if (
      error.message &&
      (error.message.includes('Request failed') ||
        error.message.includes('Network Error') ||
        error.message.includes('403') ||
        error.message.includes('Forbidden') ||
        error.message.includes('Invalid credentials') ||
        error.message.includes('axios'))
    ) {
      return
    }

    this.setState({
      hasError: true,
      error,
      errorInfo,
    })

    // Log error to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('Error caught by boundary:', error, errorInfo)
    }

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo)
    }

    // Send error to error reporting service in production
    if (process.env.NODE_ENV === 'production') {
      this.reportError(error, errorInfo)
    }
  }

  private reportError = (error: Error, errorInfo: ErrorInfo) => {
    try {
      const errorData = {
        event: 'error_occurred',
        properties: {
          error: error.message,
          context: 'React Error Boundary',
          stack: error.stack,
          componentStack: errorInfo.componentStack,
          url: window.location.href,
          userAgent: navigator.userAgent,
        },
      }

      void post('/ui/analytics/track', errorData).catch(() => {
        // Silently fail if error reporting fails
      })
    } catch (reportingError) {
      console.error('Failed to report error:', reportingError)
    }
  }

  private handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    })
  }

  private handleGoHome = () => {
    window.location.href = '/provider/home'
  }

  private handleContactSupport = () => {
    const subject = encodeURIComponent('Help needed - SolvaPay')
    const body = encodeURIComponent(
      `Hi there,

I encountered an issue while using SolvaPay. Here are the details:

What I was trying to do:
[Please describe what you were doing]

Error details:
- Error: ${this.state.error?.message}
- Page: ${window.location.href}
- Time: ${new Date().toLocaleString()}

Thanks for your help!`,
    )

    window.open(`mailto:support@solvapay.com?subject=${subject}&body=${body}`)
  }

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback
      }

      // Default error UI
      return (
        <div className="flex min-h-screen items-center justify-center bg-surface-subtle px-4">
          <div className={cn('mx-auto w-full max-w-7xl px-4', 'max-w-[640px]')}>
            <div className={cn('flex flex-col items-center', 'gap-8 text-center')}>
              {/* Error Icon */}
              <div className="mb-4 text-6xl opacity-80" style={{ filter: 'grayscale(0.3)' }}>
                🤔
              </div>

              {/* Error Title */}
              <h2
                className={cn(
                  'font-semibold text-content-primary tracking-tight',
                  'text-2xl font-normal leading-relaxed text-content-secondary',
                )}
              >
                Something went wrong
              </h2>

              {/* Error Message */}
              <p
                className={cn(
                  'text-content-secondary',
                  'max-w-md text-base leading-relaxed text-content-muted',
                )}
              >
                {`Don't worry, this happens sometimes. We're here to help you get back on track.`}
              </p>

              {/* Action Buttons */}
              <div className={cn('flex flex-col items-center', 'w-full max-w-sm gap-4')}>
                <Button
                  leftIcon={<Renew />}
                  colorScheme="accent"
                  size="lg"
                  className="w-full py-6"
                  onClick={this.handleRetry}
                >
                  Try again
                </Button>

                <Button
                  leftIcon={<Home />}
                  variant="outline"
                  size="lg"
                  className="w-full py-6"
                  onClick={this.handleGoHome}
                >
                  Go home
                </Button>

                <Button
                  leftIcon={<Email />}
                  variant="ghost"
                  size="md"
                  className="text-content-muted"
                  onClick={this.handleContactSupport}
                >
                  Need help? Contact us
                </Button>
              </div>

              {/* Helpful Tips */}
              <div className="w-full max-w-md rounded-lg border border-blue-100 bg-blue-50 p-6">
                <p className={cn('text-content-secondary', 'mb-3 font-medium text-blue-800')}>
                  Quick tips:
                </p>
                <div className={cn('flex flex-col items-center', 'items-start gap-2')}>
                  <p className={cn('text-content-secondary', 'text-sm text-blue-700')}>
                    • Try refreshing the page
                  </p>
                  <p className={cn('text-content-secondary', 'text-sm text-blue-700')}>
                    • Check your internet connection
                  </p>
                  <p className={cn('text-content-secondary', 'text-sm text-blue-700')}>
                    • Contact us if the problem continues
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

export default ErrorBoundary
